import {
  FLOW_ID_PATTERN,
  IDENTITY_PROXY_PATH,
  SESSION_AALS,
  IdentityBrowserContractError,
  boundedMessages,
  boundedNodeLabel,
  exactQuery,
  invalid,
  isRecord,
  optionalBoolean,
  optionalPositiveInteger,
  optionalString,
  requiredPositiveInteger,
  requiredString,
  validateAttributeKeys,
  validateExactKeys,
} from "./kratos-contract-primitives.js";
import {
  decodeSecurityImageNode,
  decodeSecurityTextNode,
  decodeVerificationCompletion,
} from "./kratos-security-ui-contract.js";
import {
  decodeAccountAvatarColor,
  decodeAccountAvatarReference,
} from "./avatar-reference.js";
import { accountRouteForPathname } from "./account-route-contract.js";
import {
  MAX_ACCOUNT_EMAIL_ADDRESSES,
  MAX_ACCOUNT_EMAIL_LENGTH,
  decodeAccountEmailAddresses,
} from "./email-address-contract.js";

export { IdentityBrowserContractError } from "./kratos-contract-primitives.js";
export {
  decodeActiveSession,
  decodeOtherSessions,
  decodeOtherSessionsRevocation,
  decodeSessionError,
  otherSessionRevokeUrl,
} from "./kratos-session-contract.js";

export const DISCORD_OIDC_CONSENT_CONTROL = Object.freeze({
  name: "upstream_parameters.prompt",
  value: "consent",
  disabled: false,
});

// Kratos v26.2 converts an unknown OIDC login into registration and merges the
// submitted login traits over the provider mapper. Keep the optional display
// name present but schema-invalid so login can never create an account without
// first returning to FishyStuff's explicit registration experience.
export const DISCORD_LOGIN_REGISTRATION_GUARD_CONTROL = Object.freeze({
  name: "traits.display_name",
  value: "",
  disabled: false,
});

const FLOW_VIEWS = Object.freeze([
  "login",
  "registration",
  "settings",
  "recovery",
  "verification",
]);
const POPUP_OPERATION_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const DESKTOP_AUTH_UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const MAX_DESKTOP_RETURN_CODE_LENGTH = 8 * 1024;
const MAX_DESKTOP_PASSKEY_ASSERTION_LENGTH = 12 * 1024;
const MAX_DESKTOP_LOGIN_IDENTIFIER_LENGTH = 512;
const MAX_DESKTOP_LOGIN_PASSWORD_LENGTH = 8 * 1024;
export const IDENTITY_POPUP_COMPLETION_PATH = "/account/identity-complete/";
const ACCOUNT_LINKING_MESSAGE_ID = 1010016;
const ADMIN_IDENTITY_RETURN_PATHS = new Set([
  "/admin/accounts/",
  "/de-DE/admin/konten/",
]);
const ACCOUNT_LINKING_MESSAGE_KEYS = new Set(["context", "id", "text", "type"]);
const ACCOUNT_LINKING_CONTEXT_KEYS = new Set([
  "available_credential_types",
  "available_providers",
  "duplicateIdentifier",
  "duplicate_identifier",
  "newLoginUrl",
  "new_login_url",
  "provider",
]);
const SUPPORTED_GROUPS = new Set([
  "default",
  "profile",
  "password",
  "oidc",
  "passkey",
  "webauthn",
  "code",
  "totp",
  "lookup_secret",
]);
const INPUT_ATTRIBUTE_KEYS = new Set([
  "autocomplete",
  "disabled",
  "maxlength",
  "name",
  "node_type",
  "onclick",
  "onclickTrigger",
  "onload",
  "onloadTrigger",
  "pattern",
  "required",
  "type",
  "value",
]);
const SCRIPT_ATTRIBUTE_KEYS = new Set([
  "async",
  "crossorigin",
  "id",
  "integrity",
  "node_type",
  "nonce",
  "referrerpolicy",
  "src",
  "type",
]);
const PASSKEY_REMOVE_MESSAGE_ID = 1050020;
const WEBAUTHN_REMOVE_MESSAGE_ID = 1050018;
const PASSKEY_REMOVE_META_KEYS = new Set(["label"]);
const PASSKEY_REMOVE_LABEL_KEYS = new Set(["context", "id", "text", "type"]);
const PASSKEY_REMOVE_CONTEXT_KEYS = new Set([
  "added_at",
  "added_at_unix",
  "display_name",
]);
// Kratos validates these bounds from the canonical identity schema but does not
// currently project JSON Schema maxLength values into profile UI nodes. Keep the
// browser control projection bounded even when that optional attribute is absent.
const IDENTITY_TRAIT_MAX_LENGTHS = Object.freeze({
  "traits.username": 32,
  "traits.display_name": 80,
  "traits.avatar_url": 512,
  "traits.avatar_color": 9,
});

export const ORY_WEBAUTHN_SCRIPT_INTEGRITY =
  "sha512-Dg0gN3fy+JoKxRp9Zda/4KYn3SlMdaKjs3fK5g6nDVQ/CVakD1dfMQyvRtJeiAtzSMEFviJbBLcVSrsBPGsFBA==";

export const MAX_IDENTITY_RESPONSE_BYTES = 64 * 1024;

function canonicalHttpOrigin(value, label) {
  let url;
  try {
    url = new URL(requiredString(value, label, { maxLength: 2048 }));
  } catch (_error) {
    invalid(`${label} is not a URL`);
  }
  if (
    !["http:", "https:"].includes(url.protocol)
    || url.username
    || url.password
    || url.pathname !== "/"
    || url.search
    || url.hash
  ) {
    invalid(`${label} must be an HTTP origin`);
  }
  return url.origin;
}

function sameOriginReturnUrl(locationLike, returnPath) {
  const locationOrigin = canonicalHttpOrigin(`${locationLike?.origin ?? ""}/`, "Page origin");
  let url;
  try {
    url = new URL(requiredString(returnPath, "Account return path", { maxLength: 512 }), locationOrigin);
  } catch (_error) {
    invalid("Account return path is invalid");
  }
  if (
    url.origin !== locationOrigin
    || (
      !accountRouteForPathname(url.pathname)
      && !ADMIN_IDENTITY_RETURN_PATHS.has(url.pathname)
    )
    || url.search
    || url.hash
  ) {
    invalid("Account return URL must be one exact localized account path on this origin");
  }
  return url.toString();
}

const PREVIEW_LOGIN_RETURN_HOST =
  /^pr-[1-9][0-9]*\.preview\.fishystuff\.fish$/;

function previewLoginReturnUrl(value) {
  let url;
  try {
    url = new URL(requiredString(value, "Preview login return URL", {
      maxLength: 2048,
    }));
  } catch (_error) {
    invalid("Preview login return URL is not a URL");
  }
  if (
    url.protocol !== "https:"
    || !PREVIEW_LOGIN_RETURN_HOST.test(url.hostname)
    || url.port
    || url.username
    || url.password
    || url.hash
  ) {
    invalid("Preview login return URL is outside a FishyStuff preview");
  }
  return value;
}

function browserStartUrl(identityBaseUrl, view, returnUrl, parameters = {}) {
  return `${identityBaseUrl}/self-service/${view}/browser?${new URLSearchParams({
    ...parameters,
    return_to: returnUrl,
  })}`;
}

function registrationEntry(value, context) {
  let requestUrl;
  try {
    requestUrl = new URL(requiredString(value.request_url, "Registration request URL", {
      maxLength: 2048,
    }));
  } catch (_error) {
    invalid("Registration request URL is invalid");
  }
  if (
    requestUrl.origin !== context.identityOrigin
    || requestUrl.username
    || requestUrl.password
    || requestUrl.hash
  ) {
    invalid("Registration request URL is outside the identity service");
  }
  if (requestUrl.pathname === "/self-service/registration/browser") {
    exactQuery(requestUrl, "return_to", context.returnUrl, "Registration request URL");
    return "direct";
  }
  if (requestUrl.pathname === "/self-service/login/browser") {
    exactQuery(requestUrl, "return_to", context.loginReturnUrl, "Registration request URL");
    return "discordLoginFallback";
  }
  invalid("Registration request source is unsupported", "unsupported");
}

function isSettingsRefreshContinuation(value, context) {
  let returnUrl;
  try {
    returnUrl = new URL(value);
  } catch (_error) {
    return false;
  }
  const entries = [...returnUrl.searchParams.entries()];
  return returnUrl.origin === context.identityOrigin
    && !returnUrl.username
    && !returnUrl.password
    && returnUrl.pathname === `${IDENTITY_PROXY_PATH}/self-service/settings`
    && !returnUrl.hash
    && entries.length === 1
    && entries[0][0] === "flow"
    && FLOW_ID_PATTERN.test(entries[0][1]);
}

function configuredPasskeyRpId(value, pageHostname) {
  const rpId = requiredString(value, "Identity passkey RP ID", { maxLength: 253 });
  if (
    rpId !== rpId.toLowerCase()
    || (rpId !== "localhost" && !rpId.includes("."))
    || !/^[a-z0-9](?:[a-z0-9.-]*[a-z0-9])?$/.test(rpId)
    || rpId.split(".").some((label) => (
      label.length === 0
      || label.length > 63
      || label.startsWith("-")
      || label.endsWith("-")
    ))
    || (pageHostname !== rpId && !pageHostname.endsWith(`.${rpId}`))
  ) {
    invalid("Identity passkey RP ID is invalid for this account host");
  }
  return rpId;
}

function configuredIdentityBaseUrl(value, apiOrigin, pageHostname) {
  if (value === undefined || value === null || String(value).trim() === "") {
    return `${apiOrigin}${IDENTITY_PROXY_PATH}`;
  }
  let url;
  try {
    url = new URL(requiredString(value, "Identity browser base URL", { maxLength: 512 }));
  } catch (_error) {
    invalid("Identity browser base URL is invalid");
  }
  const sharedAuthority = (
    url.protocol === "https:"
    && url.hostname === "id.fishystuff.fish"
    && (
      pageHostname === "fishystuff.fish"
      || pageHostname.endsWith(".fishystuff.fish")
    )
  );
  const localAuthority = (
    url.protocol === "http:"
    && url.hostname === "localhost"
    && pageHostname === "localhost"
  );
  if (
    (!sharedAuthority && !localAuthority)
    || !["/identity", "/identity/"].includes(url.pathname)
    || url.username
    || url.password
    || url.search
    || url.hash
  ) {
    invalid("Identity browser base URL is outside an approved FishyStuff authority");
  }
  return `${url.origin}/identity`;
}

export function identityBrowserContext({
  apiBaseUrl,
  identityBaseUrl: configuredBaseUrl,
  locationLike,
  passkeyRpId,
  returnPath = "/account/",
}) {
  const apiOrigin = canonicalHttpOrigin(apiBaseUrl, "API base URL");
  const pageOrigin = canonicalHttpOrigin(`${locationLike?.origin ?? ""}/`, "Page origin");
  const apiUrl = new URL(apiOrigin);
  const pageUrl = new URL(pageOrigin);
  if (
    [apiUrl.hostname, pageUrl.hostname].some((hostname) => (
      hostname === "localhost" || hostname === "127.0.0.1"
    ))
    && (apiUrl.hostname !== "localhost" || pageUrl.hostname !== "localhost")
  ) {
    invalid("Local identity and account origins must use localhost for passkeys");
  }
  const identityBaseUrl = configuredIdentityBaseUrl(
    configuredBaseUrl,
    apiOrigin,
    pageUrl.hostname,
  );
  const returnUrl = sameOriginReturnUrl(locationLike, returnPath);
  const loginReturnUrl = returnUrl;
  const expectedPasskeyRpId = configuredPasskeyRpId(passkeyRpId, pageUrl.hostname);
  return Object.freeze({
    apiOrigin,
    pageOrigin,
    pageHostname: pageUrl.hostname,
    passkeyRpId: expectedPasskeyRpId,
    identityBaseUrl,
    identityOrigin: new URL(identityBaseUrl).origin,
    returnUrl,
    loginReturnUrl,
    sessionUrl: `${identityBaseUrl}/sessions/whoami`,
    sessionsUrl: `${identityBaseUrl}/sessions`,
    loginStartUrl: browserStartUrl(identityBaseUrl, "login", loginReturnUrl),
    refreshLoginStartUrl: browserStartUrl(identityBaseUrl, "login", returnUrl, {
      refresh: "true",
    }),
    stepUpLoginStartUrl: browserStartUrl(identityBaseUrl, "login", returnUrl, {
      aal: "aal2",
    }),
    registrationStartUrl: browserStartUrl(identityBaseUrl, "registration", returnUrl),
    settingsStartUrl: browserStartUrl(identityBaseUrl, "settings", returnUrl),
    recoveryStartUrl: browserStartUrl(identityBaseUrl, "recovery", returnUrl),
    verificationStartUrl: browserStartUrl(identityBaseUrl, "verification", returnUrl),
    logoutFlowUrl: `${identityBaseUrl}/self-service/logout/browser`,
  });
}

export function identityPopupContext({
  apiBaseUrl,
  identityBaseUrl,
  locationLike,
  operationId,
  passkeyRpId,
}) {
  identityPopupOperationId(operationId);
  const base = identityBrowserContext({
    apiBaseUrl,
    identityBaseUrl,
    locationLike,
    passkeyRpId,
    returnPath: "/account/",
  });
  const returnUrl = new URL(IDENTITY_POPUP_COMPLETION_PATH, base.pageOrigin);
  returnUrl.searchParams.set("operation", operationId);
  const completionUrl = returnUrl.toString();
  return Object.freeze({
    ...base,
    returnUrl: completionUrl,
    loginReturnUrl: completionUrl,
    loginStartUrl: browserStartUrl(base.identityBaseUrl, "login", completionUrl),
    refreshLoginStartUrl: browserStartUrl(base.identityBaseUrl, "login", completionUrl, {
      refresh: "true",
    }),
    stepUpLoginStartUrl: browserStartUrl(base.identityBaseUrl, "login", completionUrl, {
      aal: "aal2",
    }),
    registrationStartUrl: browserStartUrl(
      base.identityBaseUrl,
      "registration",
      completionUrl,
    ),
    settingsStartUrl: browserStartUrl(base.identityBaseUrl, "settings", completionUrl),
  });
}

export function identityPopupOperationId(value) {
  if (typeof value !== "string" || !POPUP_OPERATION_PATTERN.test(value)) {
    invalid("Identity popup operation ID is invalid");
  }
  return value;
}

export function identityPopupRegistrationContinuationFromLocation(locationLike) {
  const entries = [...new URLSearchParams(String(locationLike?.search ?? "")).entries()];
  const names = new Set(entries.map(([name]) => name));
  if (!names.has("operation")) return null;
  const operationId = entries.find(([name]) => name === "operation")?.[1] ?? "";
  const flowId = entries.find(([name]) => name === "flow")?.[1] ?? "";
  if (
    entries.length !== 2
    || names.size !== 2
    || !names.has("flow")
    || !POPUP_OPERATION_PATTERN.test(operationId)
    || !FLOW_ID_PATTERN.test(flowId)
  ) {
    invalid("Discord registration continuation query is invalid");
  }
  return Object.freeze({ flowId, operationId });
}

export function identityPopupOperationFromReturnUrl(value, pageOrigin) {
  let url;
  try {
    url = new URL(requiredString(value, "Identity popup return URL", { maxLength: 2048 }));
  } catch (_error) {
    invalid("Identity popup return URL is invalid");
  }
  const entries = [...url.searchParams.entries()];
  if (
    url.origin !== pageOrigin
    || url.pathname !== IDENTITY_POPUP_COMPLETION_PATH
    || url.username
    || url.password
    || url.hash
    || entries.length !== 1
    || entries[0][0] !== "operation"
    || !POPUP_OPERATION_PATTERN.test(entries[0][1])
  ) {
    invalid("Identity popup return URL is outside the completion contract");
  }
  return entries[0][1];
}

function desktopAuthCallbackUrl(value) {
  let parsed;
  try {
    parsed = new URL(value);
  } catch (_error) {
    invalid("Desktop auth callback is invalid");
  }
  const callbackId = parsed.pathname.startsWith("/fishystuff-auth/")
    ? parsed.pathname.slice("/fishystuff-auth/".length)
    : "";
  if (
    parsed.protocol !== "http:"
    || parsed.hostname !== "127.0.0.1"
    || !parsed.port
    || parsed.username
    || parsed.password
    || parsed.search
    || parsed.hash
    || !DESKTOP_AUTH_UUID_PATTERN.test(callbackId)
  ) {
    invalid("Desktop auth callback is invalid");
  }
  return parsed.toString();
}

export function decodeDesktopAuthRequest(value) {
  let url;
  try {
    url = value instanceof URL ? value : new URL(value);
  } catch (_error) {
    invalid("Desktop sign-in request is invalid");
  }
  const production = url.protocol === "https:"
    && url.hostname === "fishystuff.fish"
    && !url.port;
  const local = url.protocol === "http:"
    && url.hostname === "localhost"
    && Boolean(url.port);
  if (
    (!production && !local)
    || url.pathname !== "/desktop-auth/"
    || url.username
    || url.password
    || url.hash
  ) {
    invalid("Desktop sign-in request is outside the completion page contract");
  }

  const entries = [...url.searchParams.entries()];
  const names = new Set(entries.map(([name]) => name));
  const exactNames = (...expected) => (
    entries.length === expected.length
    && names.size === expected.length
    && expected.every((name) => names.has(name))
  );
  const nonce = url.searchParams.get("nonce") ?? "";

  const callbackUrl = () => {
    return desktopAuthCallbackUrl(url.searchParams.get("callback") ?? "");
  };

  const completion = (extra = {}) => {
    const returnToCode = url.searchParams.get("code") ?? "";
    if (
      !DESKTOP_AUTH_UUID_PATTERN.test(nonce)
      || !returnToCode
      || returnToCode.length > MAX_DESKTOP_RETURN_CODE_LENGTH
      || /[\x00-\x1f\x7f]/.test(returnToCode)
    ) {
      invalid("Desktop sign-in completion code is invalid");
    }
    return Object.freeze({ kind: "completion", nonce, returnToCode, ...extra });
  };

  const flow = (extra = {}) => {
    const flowId = url.searchParams.get("flow") ?? "";
    if (
      !DESKTOP_AUTH_UUID_PATTERN.test(flowId)
      || !DESKTOP_AUTH_UUID_PATTERN.test(nonce)
    ) {
      invalid("Desktop sign-in request is invalid");
    }
    return Object.freeze({ kind: "flow", nonce, flowId, ...extra });
  };

  if (exactNames("nonce", "callback", "code")) {
    return completion({ callbackUrl: callbackUrl() });
  }
  if (exactNames("flow", "nonce", "callback")) {
    return flow({ callbackUrl: callbackUrl() });
  }

  invalid("Desktop sign-in request is invalid");
}

export function desktopAuthReturnUrl(request, exactOrigin) {
  if (
    request?.kind !== "flow"
    || !DESKTOP_AUTH_UUID_PATTERN.test(request.nonce ?? "")
    || !DESKTOP_AUTH_UUID_PATTERN.test(request.flowId ?? "")
  ) {
    invalid("Desktop sign-in return request is invalid");
  }
  const origin = canonicalHttpOrigin(`${exactOrigin}/`, "Desktop sign-in return origin");
  const callbackUrl = desktopAuthCallbackUrl(request.callbackUrl);
  const returnUrl = new URL("/desktop-auth/", origin);
  returnUrl.searchParams.append("nonce", request.nonce);
  // Rust first encodes this nested callback in the return URL, then encodes
  // that complete URL as Kratos's outer `return_to` query value. Kratos's JSON
  // flow retains the inner encoding, so this boundary must do the same.
  returnUrl.searchParams.append("callback", callbackUrl);
  return returnUrl.toString();
}

export function desktopPasskeyAssertionCapability(request, exactOrigin, passkeyLogin) {
  if (
    request?.kind !== "flow"
    || !DESKTOP_AUTH_UUID_PATTERN.test(request.nonce ?? "")
    || !DESKTOP_AUTH_UUID_PATTERN.test(request.flowId ?? "")
  ) {
    invalid("Desktop passkey assertion request is invalid");
  }
  const origin = canonicalHttpOrigin(`${exactOrigin}/`, "Desktop passkey origin");
  const assertion = requiredString(passkeyLogin, "Desktop passkey assertion", {
    maxLength: MAX_DESKTOP_PASSKEY_ASSERTION_LENGTH,
  });
  let credential;
  try {
    credential = JSON.parse(assertion);
  } catch (_error) {
    invalid("Desktop passkey assertion is invalid");
  }
  if (!isRecord(credential) || credential.type !== "public-key") {
    invalid("Desktop passkey assertion is invalid");
  }
  return Object.freeze({
    schemaVersion: 1,
    kind: "passkey",
    nonce: request.nonce,
    exactOrigin: origin,
    flowId: request.flowId,
    passkeyLogin: assertion,
  });
}

export function desktopPasswordLoginCapability(request, exactOrigin, identifier, password) {
  if (
    request?.kind !== "flow"
    || !DESKTOP_AUTH_UUID_PATTERN.test(request.nonce ?? "")
    || !DESKTOP_AUTH_UUID_PATTERN.test(request.flowId ?? "")
  ) {
    invalid("Desktop password login request is invalid");
  }
  const loginIdentifier = requiredString(identifier, "Desktop login identifier", {
    maxLength: MAX_DESKTOP_LOGIN_IDENTIFIER_LENGTH,
  });
  const loginPassword = requiredString(password, "Desktop login password", {
    maxLength: MAX_DESKTOP_LOGIN_PASSWORD_LENGTH,
  });
  if (/[\r\n]/.test(loginPassword)) invalid("Desktop login password is invalid");
  return Object.freeze({
    schemaVersion: 1,
    kind: "password",
    nonce: request.nonce,
    exactOrigin: canonicalHttpOrigin(`${exactOrigin}/`, "Desktop password login origin"),
    flowId: request.flowId,
    identifier: loginIdentifier,
    password: loginPassword,
  });
}

export function desktopProviderCompletionCapability(request, exactOrigin) {
  if (
    request?.kind !== "completion"
    || !DESKTOP_AUTH_UUID_PATTERN.test(request.nonce ?? "")
  ) {
    invalid("Desktop provider completion request is invalid");
  }
  return Object.freeze({
    schemaVersion: 1,
    kind: "provider",
    nonce: request.nonce,
    exactOrigin: canonicalHttpOrigin(`${exactOrigin}/`, "Desktop provider completion origin"),
    returnToCode: request.returnToCode,
  });
}

function nodeIsSupported(
  view,
  { group, name, type, value },
  { aal2 = false, accountLinking = false, refresh = false } = {},
) {
  if (name === "csrf_token") {
    return group === "default" && type === "hidden";
  }
  if (view === "login" && name === "identifier") {
    return group === "default"
      && (
        ["text", "email"].includes(type)
        || ((aal2 || accountLinking || refresh) && type === "hidden")
      );
  }
  if (
    ["registration", "settings"].includes(view)
    && [
      "traits.username",
      "traits.display_name",
      "traits.emails",
      "traits.avatar_url",
      "traits.avatar_color",
    ].includes(name)
  ) {
    const supportedGroup = view === "registration"
      ? ["default", "profile"].includes(group)
      : group === "profile";
    return supportedGroup
      && (
        name === "traits.emails"
          ? ["email", "text"].includes(type)
          : type === "text"
      );
  }
  if (
    view === "registration"
    && name === "transient_payload.fishystuffLegalAcceptance"
  ) {
    return group === "default" && type === "text" && value === "";
  }
  if (
    ["registration", "settings"].includes(view)
    && /^traits\.emails\.(?:0|[1-9][0-9]*)$/u.test(name)
  ) {
    return (view === "registration" ? ["default", "profile"].includes(group) : group === "profile")
      && ["email", "text"].includes(type);
  }
  if (name === "password") {
    return group === "password" && type === "password";
  }
  if (view === "settings" && name === "password_unlink") {
    return group === "password" && type === "submit" && value === "true";
  }
  if (name === "method" && value === "password") {
    return group === "password" && type === "submit";
  }
  if (view === "settings" && name === "method" && value === "profile") {
    return group === "profile" && type === "submit";
  }
  if (["login", "registration"].includes(view) && name === "provider") {
    return group === "oidc" && type === "submit" && value === "discord";
  }
  if (view === "settings" && ["link", "unlink"].includes(name)) {
    return group === "oidc" && type === "submit" && value === "discord";
  }
  if (["recovery", "verification"].includes(view) && group === "code") {
    if (name === "email") {
      return ["email", "submit"].includes(type);
    }
    if (name === "code") {
      return type === "text";
    }
    if (name === "method" && value === "code") {
      return ["hidden", "submit"].includes(type);
    }
    if (view === "recovery" && name === "recovery_address") {
      return ["text", "hidden"].includes(type);
    }
    if (view === "recovery" && name === "recovery_confirm_address") {
      return ["text", "submit"].includes(type);
    }
    if (view === "recovery" && name === "recovery_select_address") {
      return type === "submit" && value.length > 0;
    }
    if (view === "recovery" && name === "screen") {
      return type === "submit" && value === "previous";
    }
  }
  if (group === "totp") {
    if (name === "totp_code") {
      return ["login", "settings"].includes(view) && type === "text";
    }
    if (name === "method" && value === "totp") {
      return ["login", "settings"].includes(view) && type === "submit";
    }
    if (name === "totp_unlink") {
      return view === "settings" && type === "submit" && value === "true";
    }
  }
  if (group === "lookup_secret") {
    if (name === "lookup_secret") {
      return view === "login" && type === "text";
    }
    if (name === "method" && value === "lookup_secret") {
      return view === "login" && type === "submit";
    }
    if ([
      "lookup_secret_reveal",
      "lookup_secret_regenerate",
      "lookup_secret_disable",
      "lookup_secret_confirm",
    ].includes(name)) {
      return view === "settings" && type === "submit" && value === "true";
    }
  }
  if (group === "passkey") {
    if (name === "passkey_create_data") {
      return ["registration", "settings"].includes(view) && type === "hidden";
    }
    if (name === "passkey_challenge") {
      return view === "login" && type === "hidden";
    }
    if (name === "passkey_register") {
      return view === "registration" && type === "hidden";
    }
    if (name === "passkey_settings_register") {
      return view === "settings" && type === "hidden";
    }
    if (name === "passkey_login") {
      return view === "login" && type === "hidden";
    }
    if (name === "passkey_login_trigger") {
      return view === "login" && type === "button";
    }
    if (name === "passkey_register_trigger") {
      return ["registration", "settings"].includes(view) && type === "button";
    }
    if (name === "passkey_remove") {
      return view === "settings" && type === "submit" && /^[0-9a-f]{2,1024}$/.test(value);
    }
  }
  if (group === "webauthn") {
    if (name === "webauthn_register_displayname") {
      return view === "settings" && type === "text";
    }
    if (name === "webauthn_register_trigger") {
      return view === "settings" && type === "button";
    }
    if (name === "webauthn_register") {
      return view === "settings" && type === "hidden";
    }
    if (name === "webauthn_remove") {
      return view === "settings" && type === "submit" && /^[0-9a-f]{2,1024}$/.test(value);
    }
    if (name === "webauthn_login_trigger") {
      return view === "login" && type === "button";
    }
    if (name === "webauthn_login") {
      return view === "login" && type === "hidden";
    }
  }
  return false;
}

function expectedWebAuthnTriggers(view, name, { refresh = false } = {}) {
  if (view === "login" && name === "passkey_login" && !refresh) {
    return { onloadTrigger: "oryPasskeyLoginAutocompleteInit" };
  }
  if (view === "login" && name === "passkey_login_trigger") {
    return { onclickTrigger: "oryPasskeyLogin" };
  }
  if (view === "registration" && name === "passkey_register_trigger") {
    return { onclickTrigger: "oryPasskeyRegistration" };
  }
  if (view === "settings" && name === "passkey_register_trigger") {
    return { onclickTrigger: "oryPasskeySettingsRegistration" };
  }
  if (view === "settings" && name === "webauthn_register_trigger") {
    return { onclickTrigger: "oryWebAuthnRegistration" };
  }
  if (view === "login" && name === "webauthn_login_trigger") {
    return { onclickTrigger: "oryWebAuthnLogin" };
  }
  return {};
}

function validateWebAuthnTriggers(
  attributes,
  view,
  name,
  label,
  { expectedType = "browser", refresh = false } = {},
) {
  const expected = expectedWebAuthnTriggers(view, name, { refresh });
  for (const kind of ["onclickTrigger", "onloadTrigger"]) {
    const rawKind = kind === "onclickTrigger" ? "onclick" : "onload";
    const actual = optionalString(attributes[kind], `${label} ${kind}`, { maxLength: 64 });
    const raw = optionalString(attributes[rawKind], `${label} ${rawKind}`, {
      maxLength: name.startsWith("webauthn_") ? 32 * 1024 : 128,
    });
    const trigger = expected[kind] ?? "";
    // Kratos v26.2 projects passkey triggers differently at its two native
    // flow boundaries: browser flows carry the typed trigger and its exact
    // generated handler, while API flows carry only the typed trigger.
    // FishyStuff never evaluates the raw handler. The separately configured
    // WebAuthn strategy also carries both its typed name and exact generated
    // handler with the server-owned options.
    const expectedTyped = trigger;
    const expectedRaw = trigger.startsWith("oryPasskey")
      ? expectedType === "browser" ? `window.${trigger}()` : ""
      : trigger.startsWith("oryWebAuthn")
        ? `window.${trigger}(${attributes.value})`
        : "";
    if (actual !== expectedTyped || raw !== expectedRaw) {
      invalid(
        `${label} ${name.startsWith("passkey_") ? "passkey" : "WebAuthn"} trigger is unsupported`,
        "unsupported",
      );
    }
  }
  return Object.freeze({
    onclickTrigger: expected.onclickTrigger ?? "",
    onloadTrigger: expected.onloadTrigger ?? "",
  });
}

function decodePasskeyName(meta, label) {
  validateExactKeys(meta, PASSKEY_REMOVE_META_KEYS, `${label} metadata`);
  validateExactKeys(meta.label, PASSKEY_REMOVE_LABEL_KEYS, `${label} label`);
  if (meta.label.id !== PASSKEY_REMOVE_MESSAGE_ID || meta.label.type !== "info") {
    invalid(`${label} passkey label is unsupported`, "unsupported");
  }
  validateExactKeys(
    meta.label.context,
    PASSKEY_REMOVE_CONTEXT_KEYS,
    `${label} passkey context`,
  );
  const displayName = requiredString(
    meta.label.context.display_name,
    `${label} passkey name`,
    { maxLength: 200 },
  );
  requiredString(meta.label.context.added_at, `${label} passkey creation time`, {
    maxLength: 64,
  });
  requiredPositiveInteger(
    meta.label.context.added_at_unix,
    `${label} passkey creation time`,
  );
  return displayName;
}

function decodeWebAuthnName(meta, label) {
  validateExactKeys(meta, PASSKEY_REMOVE_META_KEYS, `${label} metadata`);
  validateExactKeys(meta.label, PASSKEY_REMOVE_LABEL_KEYS, `${label} label`);
  if (meta.label.id !== WEBAUTHN_REMOVE_MESSAGE_ID || meta.label.type !== "info") {
    invalid(`${label} security-key label is unsupported`, "unsupported");
  }
  validateExactKeys(
    meta.label.context,
    PASSKEY_REMOVE_CONTEXT_KEYS,
    `${label} security-key context`,
  );
  const displayName = requiredString(
    meta.label.context.display_name,
    `${label} security-key name`,
    { maxLength: 200 },
  );
  requiredString(meta.label.context.added_at, `${label} security-key creation time`, {
    maxLength: 64,
  });
  requiredPositiveInteger(
    meta.label.context.added_at_unix,
    `${label} security-key creation time`,
  );
  return displayName;
}

function decodeInputNode(
  node,
  index,
  view,
  {
    aal2 = false,
    accountLinking = false,
    refresh = false,
    expectedType = "browser",
    requireCsrfValue = true,
  } = {},
) {
  const label = `${view} UI node ${index}`;
  if (!isRecord(node) || node.type !== "input" || !isRecord(node.attributes)) {
    invalid(`${label} is unsupported`, "unsupported");
  }
  const attributes = node.attributes;
  validateAttributeKeys(attributes, INPUT_ATTRIBUTE_KEYS, label);
  if (attributes.node_type !== undefined && attributes.node_type !== "input") {
    invalid(`${label} node type is unsupported`, "unsupported");
  }
  const name = requiredString(attributes.name, `${label} name`, { maxLength: 64 });
  const trigger = validateWebAuthnTriggers(attributes, view, name, label, {
    expectedType,
    refresh,
  });
  const decoded = {
    kind: "input",
    group: requiredString(node.group, `${label} group`, { maxLength: 32 }),
    name,
    type: requiredString(attributes.type, `${label} input type`, { maxLength: 32 }),
    value: optionalString(attributes.value, `${label} value`, {
      maxLength: name.startsWith("passkey_") || name.startsWith("webauthn_")
        ? 32 * 1024
        : 4096,
    }),
    required: optionalBoolean(attributes.required, `${label} required flag`),
    disabled: optionalBoolean(attributes.disabled, `${label} disabled flag`),
    autocomplete: optionalString(attributes.autocomplete, `${label} autocomplete`, {
      maxLength: 64,
    }),
    pattern: optionalString(attributes.pattern, `${label} pattern`, { maxLength: 1024 }),
    maxlength: optionalPositiveInteger(attributes.maxlength, `${label} maximum length`),
    ...trigger,
    label: boundedNodeLabel(node.meta, label),
    messages: boundedMessages(node.messages, `${label} messages`),
  };
  if (!SUPPORTED_GROUPS.has(decoded.group)) {
    invalid(`${label} group is unsupported`, "unsupported");
  }
  if (!nodeIsSupported(view, decoded, { aal2, accountLinking, refresh })) {
    invalid(`${label} is unsupported`, "unsupported");
  }
  const traitMaxLength = IDENTITY_TRAIT_MAX_LENGTHS[decoded.name] ?? 0;
  if (traitMaxLength) {
    if (decoded.maxlength && decoded.maxlength !== traitMaxLength) {
      const traitLabel = decoded.name === "traits.display_name"
        ? "display-name"
        : decoded.name === "traits.username"
          ? "username"
          : decoded.name === "traits.avatar_url"
            ? "avatar"
            : decoded.name === "traits.avatar_color" ? "avatar-color" : "email";
      invalid(`${label} ${traitLabel} bounds are unsupported`, "unsupported");
    }
    decoded.maxlength = traitMaxLength;
  }
  if (decoded.name === "traits.emails" || decoded.name.startsWith("traits.emails.")) {
    if (decoded.maxlength && decoded.maxlength !== MAX_ACCOUNT_EMAIL_LENGTH) {
      invalid(`${label} email bounds are unsupported`, "unsupported");
    }
    decoded.maxlength = MAX_ACCOUNT_EMAIL_LENGTH;
  }
  if (decoded.name === "webauthn_register_displayname") {
    if (decoded.maxlength && decoded.maxlength !== 200) {
      invalid(`${label} security-key name bounds are unsupported`, "unsupported");
    }
    decoded.maxlength = 200;
    decoded.required = true;
  }
  if (decoded.name === "csrf_token" && requireCsrfValue && !decoded.value) {
    invalid(`${label} is missing its CSRF value`);
  }
  if (decoded.name === "passkey_remove") {
    decoded.passkeyName = decodePasskeyName(node.meta, label);
  }
  if (decoded.name === "webauthn_remove") {
    decoded.webauthnName = decodeWebAuthnName(node.meta, label);
  }
  if (decoded.name === "traits.avatar_url") {
    try {
      decodeAccountAvatarReference(decoded.value, `${label} avatar`);
    } catch (error) {
      invalid(error instanceof Error ? error.message : `${label} avatar is invalid`);
    }
  }
  if (decoded.name === "traits.avatar_color") {
    try {
      decodeAccountAvatarColor(decoded.value, `${label} avatar color`);
    } catch (error) {
      invalid(error instanceof Error ? error.message : `${label} avatar color is invalid`);
    }
  }
  return Object.freeze(decoded);
}

function boundedAccountLinkingList(value, label) {
  if (!Array.isArray(value) || value.length > 16) {
    invalid(`${label} is invalid`);
  }
  const entries = value.map((entry, index) => requiredString(
    entry,
    `${label} ${index}`,
    { maxLength: 64 },
  ));
  if (new Set(entries).size !== entries.length) {
    invalid(`${label} contains a duplicate`);
  }
}

function validateAccountLinkingMessage(value, context) {
  if (!Array.isArray(value) || value.length !== 1 || !isRecord(value[0])) {
    invalid("Account-linking login message is invalid");
  }
  const message = value[0];
  validateExactKeys(message, ACCOUNT_LINKING_MESSAGE_KEYS, "Account-linking login message");
  if (message.id !== ACCOUNT_LINKING_MESSAGE_ID || message.type !== "info") {
    invalid("Account-linking login message is unsupported", "unsupported");
  }
  requiredString(message.text, "Account-linking login message text", { maxLength: 1000 });
  validateExactKeys(
    message.context,
    ACCOUNT_LINKING_CONTEXT_KEYS,
    "Account-linking login message context",
  );
  const duplicateIdentifier = requiredString(
    message.context.duplicateIdentifier,
    "Account-linking duplicate identifier",
    { maxLength: 320 },
  );
  if (message.context.duplicate_identifier !== duplicateIdentifier) {
    invalid("Account-linking duplicate identifier aliases disagree");
  }
  if (message.context.provider !== "Discord") {
    invalid("Account-linking provider is unsupported", "unsupported");
  }
  const newLoginUrl = requiredString(
    message.context.newLoginUrl,
    "Account-linking new-login URL",
    { maxLength: 2048 },
  );
  if (message.context.new_login_url !== newLoginUrl) {
    invalid("Account-linking new-login URL aliases disagree");
  }
  let loginUrl;
  try {
    loginUrl = new URL(newLoginUrl);
  } catch (_error) {
    invalid("Account-linking new-login URL is invalid");
  }
  if (
    loginUrl.origin !== context.pageOrigin
    || loginUrl.pathname !== IDENTITY_POPUP_COMPLETION_PATH
    || loginUrl.hash
    || [...loginUrl.searchParams.entries()].length !== 1
    || loginUrl.searchParams.get("view") !== "login"
  ) {
    invalid("Account-linking new-login URL is outside the account surface");
  }
  boundedAccountLinkingList(
    message.context.available_credential_types,
    "Account-linking credential types",
  );
  boundedAccountLinkingList(
    message.context.available_providers,
    "Account-linking providers",
  );
}

function decodeScriptNode(node, index, context, view) {
  const label = `${view} UI node ${index}`;
  if (!isRecord(node) || node.type !== "script" || !isRecord(node.attributes)) {
    invalid(`${label} is unsupported`, "unsupported");
  }
  const attributes = node.attributes;
  validateAttributeKeys(attributes, SCRIPT_ATTRIBUTE_KEYS, label);
  let src;
  try {
    src = new URL(requiredString(attributes.src, `${label} source`, { maxLength: 2048 }));
  } catch (_error) {
    invalid(`${label} source is invalid`);
  }
  if (
    node.group !== "webauthn"
    || attributes.node_type !== "script"
    || attributes.id !== "webauthn_script"
    || attributes.type !== "text/javascript"
    || attributes.async !== true
    || attributes.referrerpolicy !== "no-referrer"
    || attributes.crossorigin !== "anonymous"
    || attributes.integrity !== ORY_WEBAUTHN_SCRIPT_INTEGRITY
    || src.origin !== context.identityOrigin
    || src.pathname !== `${IDENTITY_PROXY_PATH}/.well-known/ory/webauthn.js`
    || src.search
    || src.hash
  ) {
    invalid(`${label} WebAuthn helper script is unsupported`, "unsupported");
  }
  const nonce = requiredString(attributes.nonce, `${label} nonce`, { maxLength: 64 });
  if (!FLOW_ID_PATTERN.test(nonce)) {
    invalid(`${label} nonce is invalid`);
  }
  if (boundedMessages(node.messages, `${label} messages`).length !== 0) {
    invalid(`${label} script messages are unsupported`, "unsupported");
  }
  return Object.freeze({
    kind: "script",
    group: "webauthn",
    id: "webauthn_script",
    src: src.toString(),
    type: "text/javascript",
    async: true,
    referrerPolicy: "no-referrer",
    crossOrigin: "anonymous",
    integrity: ORY_WEBAUTHN_SCRIPT_INTEGRITY,
  });
}

function matchingNodes(nodes, predicate) {
  return nodes.filter(predicate);
}

function atMostOne(nodes, predicate, label) {
  const matches = matchingNodes(nodes, predicate);
  if (matches.length > 1) {
    invalid(`${label} appears more than once`);
  }
  return matches[0] ?? null;
}

function exactlyOne(nodes, predicate, label) {
  const node = atMostOne(nodes, predicate, label);
  if (!node) {
    invalid(`${label} is missing`, "unsupported");
  }
  return node;
}

function nodesInOriginalOrder(nodes, selected) {
  const selectedSet = new Set(selected.filter(Boolean));
  return Object.freeze(nodes.filter((node) => selectedSet.has(node)));
}

function parsePasskeyOptions(node, label) {
  if (!node.value) {
    invalid(`${label} is missing`, "unsupported");
  }
  try {
    const value = JSON.parse(node.value);
    if (!isRecord(value)) {
      invalid(`${label} is invalid`);
    }
    return value;
  } catch (error) {
    if (error instanceof IdentityBrowserContractError) {
      throw error;
    }
    invalid(`${label} is invalid`);
  }
}

function validatePasskeyRpId(value, context, label) {
  const rpId = requiredString(value, label, { maxLength: 253 });
  const expectedRpId = requiredString(context.passkeyRpId, "Configured passkey RP ID", {
    maxLength: 253,
  });
  if (rpId !== expectedRpId) {
    invalid(`${label} is not the FishyStuff relying party`);
  }
}

function validatePasskeyOptions(view, challenge, createData, context) {
  if (view === "login") {
    const value = parsePasskeyOptions(challenge, "Login passkey challenge");
    if (!isRecord(value.publicKey)) {
      invalid("Login passkey public-key options are invalid");
    }
    requiredString(value.publicKey.challenge, "Login passkey challenge value", {
      maxLength: 4096,
    });
    validatePasskeyRpId(value.publicKey.rpId, context, "Login passkey RP ID");
    return;
  }
  const value = parsePasskeyOptions(createData, `${view} passkey creation data`);
  const options = view === "registration" ? value.credentialOptions : value;
  if (
    !isRecord(options)
    || !isRecord(options.publicKey)
    || !isRecord(options.publicKey.rp)
  ) {
    invalid(`${view} passkey public-key options are invalid`);
  }
  if (view === "registration" && value.displayNameFieldName !== "traits.username") {
    invalid("Registration passkey display-name field is unsupported", "unsupported");
  }
  requiredString(options.publicKey.challenge, `${view} passkey challenge value`, {
    maxLength: 4096,
  });
  validatePasskeyRpId(options.publicKey.rp.id, context, `${view} passkey RP ID`);
}

function validateWebAuthnOptions(view, trigger, context) {
  const value = parsePasskeyOptions(trigger, `${view} security-key options`);
  if (!isRecord(value.publicKey)) {
    invalid(`${view} security-key public-key options are invalid`);
  }
  requiredString(value.publicKey.challenge, `${view} security-key challenge value`, {
    maxLength: 4096,
  });
  if (view === "login") {
    validatePasskeyRpId(value.publicKey.rpId, context, `${view} security-key RP ID`);
    return;
  }
  if (!isRecord(value.publicKey.rp)) {
    invalid(`${view} security-key relying party is invalid`);
  }
  validatePasskeyRpId(value.publicKey.rp.id, context, `${view} security-key RP ID`);
}

function decodeCodeFlowForm(view, nodes, csrf) {
  const controls = nodes.filter((node) => node !== csrf);
  if (
    controls.length === 0
    || controls.some((node) => node.kind !== "input" || node.group !== "code")
  ) {
    invalid(`${view} code controls are incomplete`, "unsupported");
  }
  const methodControls = controls.filter((node) => (
    node.name === "method" && node.value === "code"
  ));
  if (
    methodControls.length === 0
    || methodControls.length > 2
    || !methodControls.some((node) => node.type === "submit")
  ) {
    invalid(`${view} code method controls are incomplete`, "unsupported");
  }
  for (const name of [
    "email",
    "code",
    "recovery_address",
    "recovery_confirm_address",
    "screen",
  ]) {
    if (controls.filter((node) => node.name === name).length > 1) {
      invalid(`${view} ${name} control appears more than once`);
    }
  }
  if (!controls.some((node) => (
    (node.name === "email" && node.type === "email")
    || node.name === "code"
    || (node.name === "recovery_address" && node.type === "text")
    || (node.name === "recovery_confirm_address" && node.type === "text")
    || node.name === "recovery_select_address"
  ))) {
    invalid(`${view} code challenge is missing`, "unsupported");
  }
  return Object.freeze([Object.freeze({
    method: "code",
    nodes: nodesInOriginalOrder(nodes, [csrf, ...controls]),
  })]);
}

function decodeFlowForms(view, nodes, context, registrationEntryKind) {
  const csrf = exactlyOne(nodes, (node) => node.name === "csrf_token", `${view} CSRF control`);
  if (["recovery", "verification"].includes(view)) {
    return decodeCodeFlowForm(view, nodes, csrf);
  }
  const identifier = atMostOne(nodes, (node) => node.name === "identifier", "Login identifier");
  const username = atMostOne(nodes, (node) => node.name === "traits.username", "Username trait");
  const displayName = atMostOne(
    nodes,
    (node) => node.name === "traits.display_name",
    "Display-name trait",
  );
  const emails = atMostOne(
    nodes,
    (node) => node.name === "traits.emails",
    "Email collection trait",
  );
  const indexedEmails = matchingNodes(
    nodes,
    (node) => /^traits\.emails\.(?:0|[1-9][0-9]*)$/u.test(node.name),
  );
  if (indexedEmails.length > MAX_ACCOUNT_EMAIL_ADDRESSES) {
    invalid("Email collection contains too many addresses", "unsupported");
  }
  const orderedEmails = [...indexedEmails].sort((left, right) => (
    Number(left.name.slice("traits.emails.".length))
      - Number(right.name.slice("traits.emails.".length))
  ));
  const emailIndexes = orderedEmails.map((node) => Number(
    node.name.slice("traits.emails.".length),
  ));
  if (emailIndexes.some((index, position) => index !== position)) {
    invalid("Email collection indices are unsupported", "unsupported");
  }
  try {
    decodeAccountEmailAddresses(
      orderedEmails.map((node) => node.value),
      "Email collection",
    );
  } catch (error) {
    invalid(error instanceof Error ? error.message : "Email collection is invalid");
  }
  const avatarUrl = atMostOne(
    nodes,
    (node) => node.name === "traits.avatar_url",
    "Avatar trait",
  );
  const avatarColor = atMostOne(
    nodes,
    (node) => node.name === "traits.avatar_color",
    "Avatar-color trait",
  );
  const password = atMostOne(nodes, (node) => node.name === "password", "Password control");
  const passwordMethod = atMostOne(
    nodes,
    (node) => node.name === "method" && node.value === "password",
    "Password method",
  );
  const passwordUnlink = atMostOne(
    nodes,
    (node) => node.name === "password_unlink",
    "Password unlink method",
  );
  const profileMethod = atMostOne(
    nodes,
    (node) => node.name === "method" && node.value === "profile",
    "Profile method",
  );
  const provider = atMostOne(nodes, (node) => node.name === "provider", "Discord provider");
  const link = atMostOne(nodes, (node) => node.name === "link", "Discord link method");
  const unlink = atMostOne(nodes, (node) => node.name === "unlink", "Discord unlink method");
  const transientLegalAcceptance = atMostOne(
    nodes,
    (node) => node.name === "transient_payload.fishystuffLegalAcceptance",
    "Registration legal-acceptance projection",
  );
  if (
    transientLegalAcceptance
    && (
      view !== "registration"
      || registrationEntryKind !== "discordLoginFallback"
      || transientLegalAcceptance.kind !== "input"
      || transientLegalAcceptance.group !== "default"
      || transientLegalAcceptance.type !== "text"
      || transientLegalAcceptance.required
      || transientLegalAcceptance.disabled
      || transientLegalAcceptance.value !== ""
    )
  ) {
    invalid("Registration legal-acceptance projection is unsupported", "unsupported");
  }
  const passkeyChallenge = atMostOne(
    nodes,
    (node) => node.name === "passkey_challenge",
    "Passkey challenge",
  );
  const passkeyCreateData = atMostOne(
    nodes,
    (node) => node.name === "passkey_create_data",
    "Passkey creation data",
  );
  const passkeyLogin = atMostOne(nodes, (node) => node.name === "passkey_login", "Passkey login");
  const passkeyLoginTrigger = atMostOne(
    nodes,
    (node) => node.name === "passkey_login_trigger",
    "Passkey login trigger",
  );
  const passkeyRegister = atMostOne(
    nodes,
    (node) => node.name === "passkey_register",
    "Passkey registration",
  );
  const passkeySettingsRegister = atMostOne(
    nodes,
    (node) => node.name === "passkey_settings_register",
    "Settings passkey registration",
  );
  const passkeyRegisterTrigger = atMostOne(
    nodes,
    (node) => node.name === "passkey_register_trigger",
    "Passkey registration trigger",
  );
  const passkeyRemove = matchingNodes(nodes, (node) => node.name === "passkey_remove");
  if (passkeyRemove.length > 10) {
    invalid("Settings flow contains too many passkeys", "unsupported");
  }
  const profile = [
    username,
    displayName,
    emails,
    ...orderedEmails,
    avatarUrl,
    avatarColor,
  ].filter(Boolean);
  const hasPasskey = nodes.some((node) => node.group === "passkey");
  const webauthnRegisterDisplayName = atMostOne(
    nodes,
    (node) => node.name === "webauthn_register_displayname",
    "Security-key display name",
  );
  const webauthnRegisterTrigger = atMostOne(
    nodes,
    (node) => node.name === "webauthn_register_trigger",
    "Security-key registration trigger",
  );
  const webauthnRegister = atMostOne(
    nodes,
    (node) => node.name === "webauthn_register",
    "Security-key registration",
  );
  const webauthnLoginTrigger = atMostOne(
    nodes,
    (node) => node.name === "webauthn_login_trigger",
    "Security-key login trigger",
  );
  const webauthnLogin = atMostOne(
    nodes,
    (node) => node.name === "webauthn_login",
    "Security-key login",
  );
  const webauthnRemove = matchingNodes(nodes, (node) => node.name === "webauthn_remove");
  if (webauthnRemove.length > 10) {
    invalid("Settings flow contains too many security keys", "unsupported");
  }
  const hasWebAuthn = nodes.some((node) => node.group === "webauthn");
  const totpCode = atMostOne(nodes, (node) => node.name === "totp_code", "TOTP code");
  const totpMethod = atMostOne(
    nodes,
    (node) => node.name === "method" && node.value === "totp",
    "TOTP method",
  );
  const totpUnlink = atMostOne(nodes, (node) => node.name === "totp_unlink", "TOTP unlink");
  const totpQr = atMostOne(nodes, (node) => node.id === "totp_qr", "TOTP QR image");
  const totpSecret = atMostOne(
    nodes,
    (node) => node.id === "totp_secret_key",
    "TOTP secret",
  );
  const lookupCode = atMostOne(
    nodes,
    (node) => node.name === "lookup_secret",
    "Backup recovery code",
  );
  const lookupMethod = atMostOne(
    nodes,
    (node) => node.name === "method" && node.value === "lookup_secret",
    "Backup recovery-code method",
  );
  const lookupCodes = atMostOne(
    nodes,
    (node) => node.id === "lookup_secret_codes",
    "Backup recovery-code list",
  );
  const lookupActions = nodes.filter((node) => [
    "lookup_secret_reveal",
    "lookup_secret_regenerate",
    "lookup_secret_disable",
    "lookup_secret_confirm",
  ].includes(node.name));
  const forms = [];

  if (view === "login") {
    if (Boolean(password) !== Boolean(passwordMethod)) {
      invalid("Login password controls are incomplete", "unsupported");
    }
    if (passwordMethod) {
      if (!identifier) {
        invalid("Login password identifier is missing", "unsupported");
      }
      forms.push(Object.freeze({
        method: "password",
        nodes: nodesInOriginalOrder(nodes, [csrf, identifier, password, passwordMethod]),
      }));
    }
    if (provider) {
      forms.push(Object.freeze({
        method: "oidc",
        nodes: nodesInOriginalOrder(nodes, [csrf, provider]),
      }));
    }
    if (hasPasskey) {
      if (!identifier || !passkeyChallenge || !passkeyLogin || !passkeyLoginTrigger) {
        invalid("Login passkey controls are incomplete", "unsupported");
      }
      validatePasskeyOptions(view, passkeyChallenge, null, context);
      forms.push(Object.freeze({
        method: "passkey",
        nodes: nodesInOriginalOrder(nodes, [
          csrf,
          identifier,
          passkeyChallenge,
          passkeyLogin,
          passkeyLoginTrigger,
        ]),
      }));
    }
    if (hasWebAuthn) {
      if (!identifier || !webauthnLoginTrigger || !webauthnLogin) {
        invalid("Login security-key controls are incomplete", "unsupported");
      }
      validateWebAuthnOptions(view, webauthnLoginTrigger, context);
      forms.push(Object.freeze({
        method: "webauthn",
        nodes: nodesInOriginalOrder(nodes, [
          csrf,
          identifier,
          webauthnLoginTrigger,
          webauthnLogin,
        ]),
      }));
    }
    if (totpCode || totpMethod) {
      if (!totpCode || !totpMethod) {
        invalid("Login TOTP controls are incomplete", "unsupported");
      }
      forms.push(Object.freeze({
        method: "totp",
        nodes: nodesInOriginalOrder(nodes, [csrf, totpCode, totpMethod]),
      }));
    }
    if (lookupCode || lookupMethod) {
      if (!lookupCode || !lookupMethod) {
        invalid("Login backup recovery-code controls are incomplete", "unsupported");
      }
      forms.push(Object.freeze({
        method: "lookup_secret",
        nodes: nodesInOriginalOrder(nodes, [csrf, lookupCode, lookupMethod]),
      }));
    }
  } else if (view === "registration") {
    if (
      !username
      || !username.required
      || !displayName
      || displayName.required
      || !emails
      || emails.required
      || !avatarUrl
      || avatarUrl.required
      || !avatarColor
      || avatarColor.required
    ) {
      invalid("Registration requires a username and optional profile traits", "unsupported");
    }
    if (Boolean(password) !== Boolean(passwordMethod)) {
      invalid("Registration password controls are incomplete", "unsupported");
    }
    if (passwordMethod) {
      forms.push(Object.freeze({
        method: "password",
        nodes: nodesInOriginalOrder(nodes, [csrf, ...profile, password, passwordMethod]),
      }));
    }
    if (provider) {
      forms.push(Object.freeze({
        method: "oidc",
        nodes: nodesInOriginalOrder(nodes, [csrf, ...profile, provider]),
      }));
    }
    if (hasPasskey) {
      if (!passkeyCreateData || !passkeyRegister || !passkeyRegisterTrigger) {
        invalid("Registration passkey controls are incomplete", "unsupported");
      }
      validatePasskeyOptions(view, null, passkeyCreateData, context);
      forms.push(Object.freeze({
        method: "passkey",
        nodes: nodesInOriginalOrder(nodes, [
          csrf,
          ...profile,
          passkeyCreateData,
          passkeyRegister,
          passkeyRegisterTrigger,
        ]),
      }));
    }
  } else if (view === "settings") {
    if (profile.length > 0 || profileMethod) {
      if (!username || !displayName || !emails || !avatarUrl || !avatarColor || !profileMethod) {
        invalid("Settings profile controls are incomplete", "unsupported");
      }
      forms.push(Object.freeze({
        method: "profile",
        nodes: nodesInOriginalOrder(nodes, [csrf, ...profile, profileMethod]),
      }));
    }
    if (password || passwordMethod || passwordUnlink) {
      if (!password || !passwordMethod) {
        invalid("Settings password controls are incomplete", "unsupported");
      }
      forms.push(Object.freeze({
        method: "password",
        nodes: nodesInOriginalOrder(nodes, [csrf, password, passwordMethod, passwordUnlink]),
      }));
    }
    if (link && unlink) {
      invalid("Settings cannot link and unlink Discord in one flow", "unsupported");
    }
    if (link || unlink) {
      forms.push(Object.freeze({
        method: "oidc",
        nodes: nodesInOriginalOrder(nodes, [csrf, link, unlink]),
      }));
    }
    if (hasPasskey) {
      if (!passkeyCreateData || !passkeySettingsRegister || !passkeyRegisterTrigger) {
        invalid("Settings passkey controls are incomplete", "unsupported");
      }
      validatePasskeyOptions(view, null, passkeyCreateData, context);
      forms.push(Object.freeze({
        method: "passkey",
        nodes: nodesInOriginalOrder(nodes, [
          csrf,
          ...passkeyRemove,
          passkeyRegisterTrigger,
          passkeySettingsRegister,
          passkeyCreateData,
        ]),
      }));
    }
    if (hasWebAuthn) {
      if (
        !webauthnRegisterDisplayName
        || !webauthnRegisterTrigger
        || !webauthnRegister
      ) {
        invalid("Settings security-key controls are incomplete", "unsupported");
      }
      validateWebAuthnOptions(view, webauthnRegisterTrigger, context);
      forms.push(Object.freeze({
        method: "webauthn",
        nodes: nodesInOriginalOrder(nodes, [
          csrf,
          ...webauthnRemove,
          webauthnRegisterDisplayName,
          webauthnRegisterTrigger,
          webauthnRegister,
        ]),
      }));
    }
    const hasTotp = [totpCode, totpMethod, totpUnlink, totpQr, totpSecret].some(Boolean);
    if (hasTotp) {
      const isEnrollment = [totpCode, totpMethod, totpQr, totpSecret].every(Boolean)
        && !totpUnlink;
      const isRemoval = Boolean(totpUnlink)
        && ![totpCode, totpMethod, totpQr, totpSecret].some(Boolean);
      if (!isEnrollment && !isRemoval) {
        invalid("Settings TOTP controls are incomplete", "unsupported");
      }
      forms.push(Object.freeze({
        method: "totp",
        nodes: nodesInOriginalOrder(nodes, [
          csrf,
          totpQr,
          totpSecret,
          totpCode,
          totpMethod,
          totpUnlink,
        ]),
      }));
    }
    if (lookupCodes || lookupActions.length > 0) {
      if (lookupActions.length === 0 || lookupActions.length > 3) {
        invalid("Settings backup recovery-code controls are incomplete", "unsupported");
      }
      const actionNames = new Set(lookupActions.map((node) => node.name));
      if (actionNames.size !== lookupActions.length) {
        invalid("Settings backup recovery-code action appears more than once");
      }
      if (actionNames.has("lookup_secret_confirm") !== Boolean(lookupCodes)) {
        const revealingExisting = Boolean(lookupCodes)
          && actionNames.has("lookup_secret_regenerate")
          && actionNames.has("lookup_secret_disable");
        if (!revealingExisting) {
          invalid("Settings backup recovery-code list is unowned", "unsupported");
        }
      }
      forms.push(Object.freeze({
        method: "lookup_secret",
        nodes: nodesInOriginalOrder(nodes, [csrf, lookupCodes, ...lookupActions]),
      }));
    }
  }

  if (forms.length === 0) {
    invalid(`${view} flow has no supported submission method`, "unsupported");
  }
  const includedNodes = new Set(forms.flatMap((form) => form.nodes));
  if (transientLegalAcceptance) includedNodes.add(transientLegalAcceptance);
  if (includedNodes.size !== nodes.length) {
    invalid(`${view} flow contains an unowned control`, "unsupported");
  }
  return Object.freeze(forms);
}

export function decodeAccountFlow(
  value,
  context,
  route,
  { now = Date.now(), expectedType = "browser" } = {},
) {
  if (
    !isRecord(route)
    || !FLOW_VIEWS.includes(route.view)
    || !FLOW_ID_PATTERN.test(route.flowId)
  ) {
    invalid("Account flow route is invalid");
  }
  const view = route.view;
  const accountLinking = route.accountLinking === true;
  if (route.accountLinking !== undefined && (!accountLinking || view !== "login")) {
    invalid("Account-linking flow route is invalid");
  }
  if (!isRecord(value) || !isRecord(value.ui) || !Array.isArray(value.ui.nodes)) {
    invalid(`${view} flow is invalid`);
  }
  const id = requiredString(value.id, `${view} flow ID`, { maxLength: 64 });
  if (id !== route.flowId || !FLOW_ID_PATTERN.test(id)) {
    invalid(`${view} flow identity does not match the account page`);
  }
  if (!["api", "browser"].includes(expectedType) || value.type !== expectedType) {
    invalid(`Only Kratos ${expectedType} ${view} flows are accepted`, "unsupported");
  }
  const expiresAt = Date.parse(requiredString(value.expires_at, `${view} flow expiry`, {
    maxLength: 64,
  }));
  if (!Number.isFinite(expiresAt)) {
    invalid(`${view} flow expiry is invalid`);
  }
  if (expiresAt <= now) {
    invalid(`${view} flow has expired`, "expired");
  }
  if (value.state === "passed_challenge") {
    if (view !== "verification") {
      invalid(`${view} completed challenge projection is unsupported`, "unsupported");
    }
    if (value.return_to !== undefined && value.return_to !== context.returnUrl) {
      invalid(`${view} flow return URL is not this localized account page`);
    }
    return decodeVerificationCompletion(value, context, id, expiresAt);
  }
  let refresh = false;
  let requestedAal = "";
  const registrationEntryKind = view === "registration"
    ? registrationEntry(value, context)
    : "direct";
  if (view === "login") {
    if (typeof value.refresh !== "boolean") {
      invalid("Login refresh metadata is invalid");
    }
    refresh = value.refresh;
    requestedAal = requiredString(value.requested_aal, "Login requested AAL", {
      maxLength: 4,
    });
    if (!SESSION_AALS.has(requestedAal)) {
      invalid("Login requested AAL is unsupported", "unsupported");
    }
  }
  const previewReturnUrl = view === "login"
    && !refresh
    && requestedAal === "aal1"
    && value.return_to !== undefined
    && value.return_to !== context.loginReturnUrl
    ? previewLoginReturnUrl(value.return_to)
    : null;
  const expectedReturnUrl = view === "login" && !refresh && requestedAal === "aal1"
    ? previewReturnUrl ?? context.loginReturnUrl
    : view === "registration" && registrationEntryKind === "discordLoginFallback"
      ? context.loginReturnUrl
      : context.returnUrl;
  const nativeSettingsRefresh = view === "login"
    && refresh
    && isSettingsRefreshContinuation(value.return_to, context);
  if (
    value.return_to !== undefined
    && value.return_to !== expectedReturnUrl
    && !nativeSettingsRefresh
  ) {
    invalid(`${view} flow return URL is outside the expected account destination`);
  }

  let action;
  try {
    action = new URL(requiredString(value.ui.action, `${view} form action`, { maxLength: 2048 }));
  } catch (_error) {
    invalid(`${view} form action is invalid`);
  }
  if (
    action.origin !== context.identityOrigin
    || action.pathname !== `${IDENTITY_PROXY_PATH}/self-service/${view}`
    || action.hash
  ) {
    invalid(`${view} form action is outside the identity proxy`);
  }
  const actionEntries = [...action.searchParams.entries()];
  const actionFlowId = actionEntries.length === 1 && actionEntries[0][0] === "flow"
    ? actionEntries[0][1]
    : "";
  const rotatedSettingsFlow = view === "settings"
    && value.state === "success"
    && FLOW_ID_PATTERN.test(actionFlowId);
  if (actionFlowId !== id && !rotatedSettingsFlow) {
    invalid(`${view} form action query is invalid`);
  }
  if (actionEntries.length !== 1 || !FLOW_ID_PATTERN.test(actionFlowId)) {
    invalid(`${view} form action query is invalid`);
  }
  if (String(value.ui.method).toUpperCase() !== "POST") {
    invalid(`${view} form method is unsupported`, "unsupported");
  }
  if (rotatedSettingsFlow) {
    if (actionFlowId === id) {
      invalid("Completed settings flow did not rotate", "unsupported");
    }
    return Object.freeze({
      view,
      id,
      action: action.toString(),
      expiresAt,
      refresh: false,
      requestedAal: "",
      registrationEntry: "direct",
      forms: Object.freeze([]),
      completion: Object.freeze({
        kind: "settingsContinuation",
        flowId: actionFlowId,
      }),
      webauthnScript: null,
      passkeyOnloadTrigger: "",
      messages: boundedMessages(value.ui.messages, `${view} flow messages`),
    });
  }
  if (value.ui.nodes.length === 0 || value.ui.nodes.length > 40) {
    invalid(`${view} flow has an unsupported UI node count`, "unsupported");
  }
  const nodes = [];
  const scripts = [];
  value.ui.nodes.forEach((node, index) => {
    if (node?.type === "input") {
      nodes.push(decodeInputNode(node, index, view, {
        aal2: requestedAal === "aal2",
        accountLinking,
        refresh,
        expectedType,
        requireCsrfValue: expectedType === "browser",
      }));
    } else if (node?.type === "img") {
      nodes.push(decodeSecurityImageNode(node, index, view));
    } else if (node?.type === "text") {
      nodes.push(decodeSecurityTextNode(node, index, view));
    } else if (node?.type === "script") {
      scripts.push(decodeScriptNode(node, index, context, view));
    } else {
      invalid(`${view} UI node ${index} is unsupported`, "unsupported");
    }
  });
  const hasWebAuthnControls = nodes.some((node) => (
    node.group === "passkey" || node.group === "webauthn"
  ));
  // Kratos browser flows project their integrity-pinned helper node. Native
  // API flows omit it; FishyStuff's desktop-auth page owns its native WebAuthn
  // ceremony and must not manufacture or accept an inline script projection.
  const expectedScriptCount = hasWebAuthnControls && expectedType === "browser" ? 1 : 0;
  if (scripts.length !== expectedScriptCount) {
    invalid(`${view} WebAuthn helper script count is unsupported`, "unsupported");
  }
  if (accountLinking) {
    const identifier = nodes.find((node) => node.name === "identifier");
    if (!identifier || identifier.type !== "hidden" || !identifier.value) {
      invalid("Account-linking login identifier is missing", "unsupported");
    }
    validateAccountLinkingMessage(value.ui.messages, context);
  }
  if (refresh) {
    const identifier = nodes.find((node) => node.name === "identifier");
    if (identifier && (identifier.type !== "hidden" || !identifier.value)) {
      invalid("Refresh-login identity is invalid", "unsupported");
    }
  }
  const forms = decodeFlowForms(view, nodes, context, registrationEntryKind);
  const onloadTrigger = nodes.find((node) => node.onloadTrigger)?.onloadTrigger ?? "";
  return Object.freeze({
    view,
    id,
    action: action.toString(),
    expiresAt,
    refresh,
    requestedAal,
    registrationEntry: registrationEntryKind,
    ...(accountLinking ? { accountLinking: true } : {}),
    forms,
    completion: null,
    webauthnScript: scripts[0] ?? null,
    passkeyOnloadTrigger: onloadTrigger,
    messages: accountLinking
      ? Object.freeze([])
      : boundedMessages(value.ui.messages, `${view} flow messages`),
  });
}

export function decodeIdentityPopupFlow(value, {
  apiBaseUrl,
  identityBaseUrl,
  locationLike,
  passkeyRpId,
  view,
  now = Date.now(),
} = {}) {
  if (!FLOW_VIEWS.includes(view)) {
    invalid("Identity popup flow view is unsupported", "unsupported");
  }
  const operationId = identityPopupOperationFromReturnUrl(
    value?.return_to,
    canonicalHttpOrigin(`${locationLike?.origin ?? ""}/`, "Page origin"),
  );
  const context = identityPopupContext({
    apiBaseUrl,
    identityBaseUrl,
    locationLike,
    operationId,
    passkeyRpId,
  });
  const flowId = requiredString(value?.id, "Identity popup flow ID", { maxLength: 64 });
  const accountLinking = view === "login"
    && Array.isArray(value?.ui?.messages)
    && value.ui.messages.length === 1
    && value.ui.messages[0]?.id === ACCOUNT_LINKING_MESSAGE_ID;
  return Object.freeze({
    operationId,
    flow: decodeAccountFlow(value, context, {
      view,
      flowId,
      ...(accountLinking ? { accountLinking: true } : {}),
    }, { now }),
  });
}

export function decodeLogoutFlow(value, context) {
  if (!isRecord(value)) {
    invalid("Logout flow is invalid");
  }
  let logoutUrl;
  try {
    logoutUrl = new URL(requiredString(value.logout_url, "Logout URL", { maxLength: 2048 }));
  } catch (_error) {
    invalid("Logout URL is invalid");
  }
  if (
    logoutUrl.origin !== context.identityOrigin
    || logoutUrl.pathname !== `${IDENTITY_PROXY_PATH}/self-service/logout`
    || logoutUrl.hash
  ) {
    invalid("Logout URL is outside the identity proxy");
  }
  const entries = [...logoutUrl.searchParams.entries()];
  if (
    entries.length !== 1
    || entries[0][0] !== "token"
    || entries[0][1].length < 16
    || entries[0][1].length > 4096
  ) {
    invalid("Logout URL token is invalid");
  }
  return Object.freeze({ logoutUrl: logoutUrl.toString() });
}

export async function readBoundedJson(response, label) {
  const contentType = String(response?.headers?.get?.("content-type") ?? "").toLowerCase();
  if (!contentType.startsWith("application/json")) {
    invalid(`${label} did not return JSON`);
  }
  const reader = response.body?.getReader?.();
  if (!reader) {
    invalid(`${label} response body is unavailable`);
  }
  const decoder = new TextDecoder("utf-8", { fatal: true });
  let bytes = 0;
  let text = "";
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }
      bytes += value.byteLength;
      if (bytes > MAX_IDENTITY_RESPONSE_BYTES) {
        await reader.cancel("identity response exceeded its byte bound");
        invalid(`${label} exceeded its byte bound`);
      }
      text += decoder.decode(value, { stream: true });
    }
    text += decoder.decode();
  } catch (error) {
    if (error instanceof IdentityBrowserContractError) {
      throw error;
    }
    invalid(`${label} could not be decoded`);
  }
  try {
    return JSON.parse(text);
  } catch (_error) {
    invalid(`${label} returned malformed JSON`);
  }
}
